import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { createDefaultSellerReportDateRange, useSellerReportFilters } from '../useSellerReportFilters'
import { loadSellerTicketAverageReport } from '../services'
import {
  SellerReportSourceFilter,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
} from '../types'
import { ConferenceOrderItem, ConferenceOrderSource } from '../../SellerQueue/types'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface SellerSourceMixRow {
  sellerKey: string
  sellerLabel: string
  totalOrders: number
  totalAmountCents: number
  ninegridOrders: number
  ninegridAmountCents: number
  totvsOrders: number
  totvsAmountCents: number
  ninegridSharePercent: number
  totvsSharePercent: number
  dominantSource: 'ninegrid' | 'totvs' | 'balanced'
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const formatCurrency = (amountCents: number): string => currencyFormatter.format(amountCents / 100)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const readString = (value: unknown): string => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return ''
}

const getSellerKeyAndLabel = (order: ConferenceOrderItem): { sellerKey: string; sellerLabel: string } => {
  const content = isRecord(order.content) ? order.content : {}
  const rawTotvsOrder = isRecord(content.rawTotvsOrder) ? content.rawTotvsOrder : {}
  const rawNinegridOrder = isRecord(content.rawNinegridOrder) ? content.rawNinegridOrder : {}
  const commissioneds = Array.isArray(rawTotvsOrder.commissioneds) ? rawTotvsOrder.commissioneds : []
  const firstCommissioned = isRecord(commissioneds[0]) ? commissioneds[0] : {}

  const sellerNameCandidates = [
    content.sellerName,
    rawNinegridOrder.sellerName,
    rawTotvsOrder.sellerName,
    firstCommissioned.sellerName,
    rawTotvsOrder.representativeName,
  ]

  const sellerCodeCandidates = [
    content.sellerCode,
    rawNinegridOrder.sellerCode,
    rawTotvsOrder.sellerCode,
    firstCommissioned.sellerCode,
    rawTotvsOrder.representativeCode,
  ]

  const sellerCpfCandidates = [
    content.sellerCpf,
    rawNinegridOrder.sellerCpf,
    rawTotvsOrder.sellerCpfCnpj,
    firstCommissioned.sellerCpfCnpj,
  ]

  const sellerName = sellerNameCandidates.map(readString).find(Boolean) || ''
  const sellerCode = sellerCodeCandidates.map(readString).find(Boolean) || ''
  const sellerCpf = sellerCpfCandidates.map(readString).find(Boolean) || ''

  const sellerLabel = sellerName || sellerCode || sellerCpf || 'Sem vendedor'
  const sellerKey = sellerCode || sellerCpf || sellerName || 'sem-vendedor'

  return {
    sellerKey,
    sellerLabel,
  }
}

const buildSourceMixRows = (report: SellerTicketAverageReport | null): SellerSourceMixRow[] => {
  if (!report?.items.length) {
    return []
  }

  const enrichedLabelBySellerKey = new Map(report.rows.map((row) => [row.sellerKey, row.sellerLabel]))

  const grouped = new Map<string, Omit<SellerSourceMixRow, 'ninegridSharePercent' | 'totvsSharePercent' | 'dominantSource'>>()

  for (const order of report.items) {
    const source = order.source
    if (source !== 'ninegrid' && source !== 'totvs') {
      continue
    }

    const { sellerKey, sellerLabel } = getSellerKeyAndLabel(order)
    const current = grouped.get(sellerKey) || {
      sellerKey,
      sellerLabel: enrichedLabelBySellerKey.get(sellerKey) || sellerLabel,
      totalOrders: 0,
      totalAmountCents: 0,
      ninegridOrders: 0,
      ninegridAmountCents: 0,
      totvsOrders: 0,
      totvsAmountCents: 0,
    }

    const orderTotal = typeof order.total === 'number' ? Math.round(order.total) : 0

    current.sellerLabel = enrichedLabelBySellerKey.get(sellerKey) || sellerLabel || current.sellerLabel
    current.totalOrders += 1
    current.totalAmountCents += orderTotal

    if (source === 'ninegrid') {
      current.ninegridOrders += 1
      current.ninegridAmountCents += orderTotal
    } else {
      current.totvsOrders += 1
      current.totvsAmountCents += orderTotal
    }

    grouped.set(sellerKey, current)
  }

  return Array.from(grouped.values())
    .map((row) => {
      const ninegridSharePercent =
        row.totalAmountCents > 0 ? (row.ninegridAmountCents / row.totalAmountCents) * 100 : 0
      const totvsSharePercent =
        row.totalAmountCents > 0 ? (row.totvsAmountCents / row.totalAmountCents) * 100 : 0

      let dominantSource: 'ninegrid' | 'totvs' | 'balanced' = 'balanced'
      if (ninegridSharePercent >= 60) {
        dominantSource = 'ninegrid'
      } else if (totvsSharePercent >= 60) {
        dominantSource = 'totvs'
      }

      return {
        ...row,
        ninegridSharePercent,
        totvsSharePercent,
        dominantSource,
      }
    })
    .sort((left, right) => right.totalAmountCents - left.totalAmountCents)
}

const sourceLabel = (source: ConferenceOrderSource | 'balanced'): string => {
  if (source === 'ninegrid') {
    return 'Ninegrid'
  }

  if (source === 'totvs') {
    return 'TOTVS'
  }

  return 'Equilibrado'
}

const sourceBadgeClass = (source: ConferenceOrderSource | 'balanced'): string => {
  if (source === 'ninegrid') {
    return 'badge badge-light-primary'
  }

  if (source === 'totvs') {
    return 'badge badge-light-warning'
  }

  return 'badge badge-light-success'
}

const exportToCSV = (filename: string, rows: SellerSourceMixRow[]): void => {
  let csv = 'sep=;\n'
  csv += 'Vendedor;Pedidos;Faturamento;Ninegrid pedidos;Ninegrid faturamento;TOTVS pedidos;TOTVS faturamento;Ninegrid (%);TOTVS (%);Mix\n'

  rows.forEach((row) => {
    csv += `"${row.sellerLabel}";${row.totalOrders};${row.totalAmountCents / 100};${row.ninegridOrders};${row.ninegridAmountCents / 100};${row.totvsOrders};${row.totvsAmountCents / 100};${row.ninegridSharePercent.toFixed(1)};${row.totvsSharePercent.toFixed(1)};${sourceLabel(row.dominantSource)}\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, rows: SellerSourceMixRow[]): void => {
  const data = rows.map((row) => ({
    Vendedor: row.sellerLabel,
    Pedidos: row.totalOrders,
    Faturamento: row.totalAmountCents / 100,
    'Ninegrid pedidos': row.ninegridOrders,
    'Ninegrid faturamento': row.ninegridAmountCents / 100,
    'TOTVS pedidos': row.totvsOrders,
    'TOTVS faturamento': row.totvsAmountCents / 100,
    'Ninegrid (%)': row.ninegridSharePercent.toFixed(1),
    'TOTVS (%)': row.totvsSharePercent.toFixed(1),
    Mix: sourceLabel(row.dominantSource),
  }))

  const worksheet = XLSX.utils.json_to_sheet(data)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Mix')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (filename: string, rows: SellerSourceMixRow[]): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Mix por Origem e Vendedor', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Resumo do Mix', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Vendedores analisados: ${rows.length} | Equilibrados: ${rows.filter((row) => row.dominantSource === 'balanced').length}`, margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Vendedor', 'Pedidos', 'Faturamento', 'Ninegrid pedidos', 'TOTVS pedidos', 'Ninegrid (%)', 'TOTVS (%)', 'Mix']],
    body: rows.map((row) => [
      row.sellerLabel,
      row.totalOrders,
      formatCurrency(row.totalAmountCents),
      row.ninegridOrders,
      row.totvsOrders,
      row.ninegridSharePercent.toFixed(1),
      row.totvsSharePercent.toFixed(1),
      sourceLabel(row.dominantSource),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const SellerSourceMixReportPage: FC = () => {
  const { filters, setFilters, isReady } = useSellerReportFilters<SellerTicketAverageFilters>({
    ...createDefaultSellerReportDateRange(),
    source: 'all',
  })
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)
      const data = await loadSellerTicketAverageReport(filters)
      setReport(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    if (isReady) {
      void loadReport()
    }
  }, [isReady, loadReport])

  const sourceOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Todas as fontes' },
      { value: 'ninegrid' as const, label: 'Ninegrid' },
      { value: 'totvs' as const, label: 'TOTVS' },
    ],
    []
  )

  const rows = useMemo(() => buildSourceMixRows(report), [report])

  const topNinegridDependency = useMemo(
    () => [...rows].sort((left, right) => right.ninegridSharePercent - left.ninegridSharePercent)[0] || null,
    [rows]
  )

  const topTotvsDependency = useMemo(
    () => [...rows].sort((left, right) => right.totvsSharePercent - left.totvsSharePercent)[0] || null,
    [rows]
  )

  const balancedSellers = rows.filter((row) => row.dominantSource === 'balanced').length

  const onSourceChange = (source: SellerReportSourceFilter) => {
    setFilters((previous) => ({
      ...previous,
      source,
    }))
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Mix por Origem e Vendedor',
            path: '/seller/reports/source-mix-seller',
            isActive: true,
          },
        ]}
      >
        Relatório de Mix por Origem e Vendedor
      </PageTitle>

      <ReportShell
        title='Mix de Origem por Vendedor'
        description='Cruza vendedor x origem para identificar dependência de canal, equilíbrio de mix e concentração de receita por Ninegrid ou TOTVS.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Mix-Origem-${timestamp}.csv`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Mix-Origem-${timestamp}.xlsx`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Mix-Origem-${timestamp}.pdf`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Fonte</label>
              <select
                className='form-select'
                value={filters.source}
                onChange={(event) => onSourceChange(event.target.value as SellerReportSourceFilter)}
              >
                {sourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Maior dependência Ninegrid</div>
                <div className='fs-5 fw-bold text-primary'>
                  {topNinegridDependency ? <SellerIdentity label={topNinegridDependency.sellerLabel} /> : 'Sem dados'}
                </div>
                <div className='mt-2 fw-semibold text-primary'>
                  {topNinegridDependency
                    ? `${percentFormatter.format(topNinegridDependency.ninegridSharePercent)}%`
                    : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Maior dependência TOTVS</div>
                <div className='fs-5 fw-bold text-warning'>
                  {topTotvsDependency ? <SellerIdentity label={topTotvsDependency.sellerLabel} /> : 'Sem dados'}
                </div>
                <div className='mt-2 fw-semibold text-warning'>
                  {topTotvsDependency
                    ? `${percentFormatter.format(topTotvsDependency.totvsSharePercent)}%`
                    : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Vendedores equilibrados</div>
                <div className='fs-2hx fw-bold text-success'>
                  {balancedSellers.toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Cruzamento vendedor x origem</span>
              <span className='text-gray-600 mt-1'>Receita, pedidos e peso relativo de cada canal por vendedor.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : rows.length > 0 ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Vendedor</th>
                      <th className='text-end'>Pedidos</th>
                      <th className='text-end'>Faturamento</th>
                      <th className='text-end'>Ninegrid</th>
                      <th className='text-end'>TOTVS</th>
                      <th className='text-end'>Mix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.sellerKey}>
                        <td>
                          <div className='fw-bold text-dark'>
                            <SellerIdentity label={row.sellerLabel} />
                          </div>
                        </td>
                        <td className='text-end fw-bold'>{row.totalOrders}</td>
                        <td className='text-end fw-bold text-success'>
                          {formatCurrency(row.totalAmountCents)}
                        </td>
                        <td className='text-end'>
                          <div className='fw-semibold text-primary'>
                            {formatCurrency(row.ninegridAmountCents)}
                          </div>
                          <div className='text-gray-600 fs-7'>
                            {row.ninegridOrders} pedidos ⬢ {percentFormatter.format(row.ninegridSharePercent)}%
                          </div>
                        </td>
                        <td className='text-end'>
                          <div className='fw-semibold text-warning'>
                            {formatCurrency(row.totvsAmountCents)}
                          </div>
                          <div className='text-gray-600 fs-7'>
                            {row.totvsOrders} pedidos ⬢ {percentFormatter.format(row.totvsSharePercent)}%
                          </div>
                        </td>
                        <td className='text-end'>
                          <span className={sourceBadgeClass(row.dominantSource)}>
                            {sourceLabel(row.dominantSource)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum vendedor encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { SellerSourceMixReportPage }
